import type { QueryClient } from "@tanstack/react-query";
import { type MutableRefObject, useCallback, useRef } from "react";
import { toast } from "@volca/ui/lib/toast";
import {
  aiScriptSpreadsheetKey,
  aiScriptSpreadsheetSheetKey,
} from "@/payroll/ai-script/query";
import type {
  IAiSpreadsheetCell,
  IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { isAiScriptWorkbookVersionConflictError } from "@/payroll/ai-script/utils";
import { flushWorkbookCellEdits } from "@/payroll/ai-script/workbook-cell-edit-batcher";
import type { MutationErrorHandlers } from "./mutation-error-feedback";
import type { ViewerMutations } from "./use-viewer-mutations";
import type { UseWorkbookHistoryResult } from "./use-workbook-history";
import { asValueType } from "./viewer-helpers";
import { replayOps, type ReplayOpMutations } from "./workbook-history-replay";

interface UseViewerUndoRedoParams {
  businessId: string | null;
  runId: string;
  isLocked: boolean;
  hasActiveSheetError: boolean;
  isWorkbookOperationPending: boolean;
  queryClient: QueryClient;
  history: UseWorkbookHistoryResult;
  mutations: ViewerMutations;
  workbookVersionRef: MutableRefObject<number | undefined>;
  activeSheet: IAiSpreadsheetSheet | undefined;
  recordWorkbookVersion: (version: number | undefined) => void;
  findLoadedCellById: (cellId: string) => IAiSpreadsheetCell | undefined;
  handleMutationError: MutationErrorHandlers["handleMutationError"];
}

export const useViewerUndoRedo = ({
  businessId,
  runId,
  isLocked,
  hasActiveSheetError,
  isWorkbookOperationPending,
  queryClient,
  history,
  mutations: {
    directUpdateCellAsync,
    bulkPasteAsync,
    createManualRowAsync,
    deleteManualRowAsync,
    restoreManualRowAsync,
    bulkDeleteRowsAsync,
    bulkRestoreRowsAsync,
  },
  workbookVersionRef,
  activeSheet,
  recordWorkbookVersion,
  findLoadedCellById,
  handleMutationError,
}: UseViewerUndoRedoParams) => {
  const applyingRef = useRef(false);

  const replayMutations: ReplayOpMutations = {
    updateCell: (params) =>
      directUpdateCellAsync({
        runId,
        sheetId: params.sheetId,
        cellId: params.cellId,
        rawInput: params.rawInput,
        kind: params.kind,
        valueType: asValueType(params.valueType),
        baseVersion: params.baseVersion,
      }),
    bulkPaste: (params) =>
      bulkPasteAsync({
        runId,
        sheetId: params.sheetId,
        edits: params.edits,
        rows: [],
        baseVersion: params.baseVersion,
      }),
    createManualRow: (params) =>
      createManualRowAsync({
        runId,
        sheetId: params.sheetId,
        rows: params.rows,
        baseVersion: params.baseVersion,
      }),
    deleteManualRow: (params) =>
      deleteManualRowAsync({
        runId,
        sheetId: params.sheetId,
        manualRowId: params.manualRowId,
        baseVersion: params.baseVersion,
      }),
    restoreManualRow: (params) =>
      restoreManualRowAsync({
        runId,
        sheetId: params.sheetId,
        manualRowId: params.manualRowId,
        baseVersion: params.baseVersion,
      }),
    bulkDeleteRows: (params) =>
      bulkDeleteRowsAsync({
        runId,
        sheetId: params.sheetId,
        manualRowIds: params.manualRowIds,
        baseVersion: params.baseVersion,
      }),
    bulkRestoreRows: (params) =>
      bulkRestoreRowsAsync({
        runId,
        sheetId: params.sheetId,
        manualRowIds: params.manualRowIds,
        baseVersion: params.baseVersion,
      }),
    resolveCellRawInput: (_sheetId, cellId) =>
      findLoadedCellById(cellId)?.raw_input ?? "",
  };

  const applyHistoryEntry = async (
    ops: Parameters<typeof replayOps>[0],
    failureTitle: string,
  ) => {
    history.setApplying(true);
    try {
      const version = await replayOps(
        ops,
        replayMutations,
        workbookVersionRef.current,
      );
      recordWorkbookVersion(version);
    } catch (mutationError) {
      history.reset();
      queryClient.invalidateQueries({
        queryKey: aiScriptSpreadsheetKey(businessId, runId),
      });
      if (activeSheet) {
        queryClient.invalidateQueries({
          queryKey: aiScriptSpreadsheetSheetKey(
            businessId,
            runId,
            activeSheet.id,
          ),
        });
      }

      if (isAiScriptWorkbookVersionConflictError(mutationError)) {
        toast.error("Couldn't apply undo/redo", {
          description:
            "The Grid changed since that edit, so it was refreshed instead. Review the current values and try again.",
        });
      } else {
        handleMutationError(failureTitle, mutationError);
      }
    } finally {
      history.setApplying(false);
      applyingRef.current = false;
    }
  };

  const isHistoryBlocked =
    isLocked ||
    hasActiveSheetError ||
    history.isApplying ||
    isWorkbookOperationPending;

  const undo = useCallback(() => {
    if (!businessId || applyingRef.current || isHistoryBlocked) return;
    applyingRef.current = true;
    void flushWorkbookCellEdits(businessId, runId)
      .then(() => {
        const entry = history.beginUndo();
        if (!entry) {
          applyingRef.current = false;
          return;
        }
        void applyHistoryEntry(entry.inverse, "Failed to undo");
      })
      .catch(() => {
        applyingRef.current = false;
      });
  }, [businessId, history, isHistoryBlocked, runId]);

  const redo = useCallback(() => {
    if (!businessId || applyingRef.current || isHistoryBlocked) return;
    applyingRef.current = true;
    void flushWorkbookCellEdits(businessId, runId)
      .then(() => {
        const entry = history.beginRedo();
        if (!entry) {
          applyingRef.current = false;
          return;
        }
        void applyHistoryEntry(entry.forward, "Failed to redo");
      })
      .catch(() => {
        applyingRef.current = false;
      });
  }, [businessId, history, isHistoryBlocked, runId]);

  return { isHistoryBlocked, undo, redo };
};
